import { File05Icon, Loading01Icon } from '@langchain/untitled-ui-icons';

import { useCallback, useEffect, useState } from 'react';
import { Panel, PanelGroup } from 'react-resizable-panels';
import { useNavigate } from 'react-router-dom';

import { Tooltip } from '@/design-system';
import { useDefaultDuration } from '@/hooks/useDefaultDuration';
import {
  useCreateAlertMutation,
  useOrganizationId,
  useRunStats,
  useUpdateAlertMutation,
} from '@/hooks/useSwr';
import {
  AlertRuleWithActions,
  CreateAlertRequest,
  UpdateAlertRequest,
} from '@/types/alerts';
import {
  appOrganizationPath,
  appProjectsPath,
  appSessionPath,
} from '@/utils/constants';

import { ProjectsSelector } from '../../Pages/SingleDashboard/components/CustomChartCrudPaneComponents';
import Banner from '../Banner';
import { DisabledTooltip } from '../DisabledTooltip';
import { Pane } from '../Pane';
import { useDebouncedFilter } from '../RunsTable/hooks/useDebouncedFilter';
import { SplitViewPane } from '../SplitViewPane';
import { AlertNotificationOptions } from './AlertNotificationOptions';
import { AlertOptions } from './AlertOptions';
import { AlertPreview } from './AlertPreview';
import { INITIAL_STATE, useAlertState } from './useAlertState';
import {
  buildRequest,
  canUpdateOrCreateAlert,
  getAlertBuilder,
  getErrorBanner,
} from './utils';

function AlertsPaneContent({
  sessionId,
  alert,
  projectName,
}: {
  sessionId: string;
  projectName?: string;
  alert?: AlertRuleWithActions;
}) {
  const { resetState, banner, setBanner } = useAlertState();

  const { defaultDurationTimeModel } = useDefaultDuration('runsTable');

  const params = useDebouncedFilter({
    runsFilter: { session: [sessionId] },
    timeModel: defaultDurationTimeModel,
    searchModel: {},
  });

  const runStatsWithoutFilters = useRunStats(params);

  useEffect(() => {
    if (alert) {
      resetState(getAlertBuilder(alert, projectName));
    } else {
      resetState({
        ...INITIAL_STATE,
        webhookConfig: {
          ...INITIAL_STATE.webhookConfig,
          project_name: projectName || '',
        },
      });
    }
    return () => {
      resetState({
        ...INITIAL_STATE,
        webhookConfig: {
          ...INITIAL_STATE.webhookConfig,
          project_name: projectName || '',
        },
      });
    };
  }, [alert, resetState, projectName]);

  return (
    <>
      <AlertOptions runStats={runStatsWithoutFilters.data} />
      <AlertNotificationOptions sessionId={sessionId} />
      {banner && (
        <Banner
          title={banner.title}
          description={banner.msg}
          open={true}
          intent={banner.status}
          closeable
          onClose={() => setBanner(undefined)}
        />
      )}
    </>
  );
}

export function AlertsCreatePane({
  isOpen,
  onClose,
  sessionId,
  projectName,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  sessionId?: string;
  projectName?: string;
  onSuccess?: () => void;
}) {
  const state = useAlertState();
  const [selectedSessionId, setSelectedSessionId] = useState<string>(
    sessionId || ''
  );

  const doClose = useCallback(() => {
    setSelectedSessionId(sessionId || '');
    onClose();
  }, [onClose, sessionId]);

  // Reset state when sessionId prop changes
  useEffect(() => {
    setSelectedSessionId(sessionId || '');
  }, [sessionId]);

  const onCreateSuccess = () => {
    onClose();
  };

  const { trigger: createAlert, isMutating: isCreating } =
    useCreateAlertMutation(selectedSessionId || '', onCreateSuccess, {
      onError: (err) =>
        state.setBanner(getErrorBanner('Error creating alert', err)),
    });

  const onSubmit = async () => {
    if (!selectedSessionId) {
      state.setBanner({
        title: 'Error creating alert',
        msg: 'Please select a project first',
        status: 'error',
      });
      return;
    }

    const request = buildRequest(state);
    if (request instanceof Error) {
      state.setBanner({
        title: 'Error creating alert',
        msg: request.message,
        status: 'error',
      });
    } else {
      await createAlert({ json: request as CreateAlertRequest });
      onSuccess?.();
    }
  };

  const isCreatingDisabled =
    !canUpdateOrCreateAlert(state) || !selectedSessionId;

  return (
    <Pane
      open={isOpen}
      onClose={doClose}
      title={
        <div className="flex items-center gap-2">
          <p>Create Alert</p>
          <Tooltip title={'Documentation'}>
            <a
              href="https://docs.langchain.com/langsmith/alerts"
              target="_blank"
              rel="noopener noreferrer"
              className="self-center rounded-md border border-secondary p-1 hover:bg-secondary"
            >
              <File05Icon className="size-4 text-tertiary" />
            </a>
          </Tooltip>
        </div>
      }
      className="px-4 pb-0 pt-0"
    >
      <div className="flex h-full">
        <div className="border-divider flex flex-1 flex-col gap-8 overflow-y-auto border-r border-secondary p-6 pb-10">
          {/* PROJECT SELECTOR */}
          <div className="flex flex-col gap-2">
            <h3 className="text-md font-medium">Tracing project</h3>
            <ProjectsSelector
              multiple={false}
              selectedProjectIds={[selectedSessionId]}
              onSelectedProjectIdsChange={(ids) =>
                setSelectedSessionId(ids[0] || '')
              }
            />
          </div>

          {selectedSessionId && (
            <AlertsPaneContent
              projectName={projectName}
              sessionId={selectedSessionId}
            />
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="text-button-secondary rounded-md border border-secondary px-3 py-1.5 text-sm font-medium hover:bg-tertiary"
              onClick={doClose}
            >
              Cancel
            </button>
            <DisabledTooltip
              disabled={isCreatingDisabled}
              disabledMsg="Please complete all fields to create this alert."
            >
              <button
                type="button"
                className="flex items-center gap-2 rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-button-primary hover:bg-brand-primary-hover disabled:pointer-events-none disabled:opacity-50"
                onClick={onSubmit}
                disabled={isCreatingDisabled}
              >
                {isCreating && (
                  <Loading01Icon className="h-4 w-4 animate-spin" />
                )}
                <span className="text-sm">Create Alert</span>
              </button>
            </DisabledTooltip>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {selectedSessionId && <AlertPreview sessionId={selectedSessionId} />}
        </div>
      </div>
    </Pane>
  );
}

export function AlertsViewPane({
  isOpen,
  onClose,
  alert,
  sessionId,
  onEditClick,
}: {
  isOpen: boolean;
  onEditClick: (alert: AlertRuleWithActions) => void;
  onClose: () => void;
  alert: AlertRuleWithActions | undefined;
  sessionId: string;
}) {
  const { resetState } = useAlertState();

  const organizationId = useOrganizationId();

  const navigate = useNavigate();

  useEffect(() => {
    if (alert) {
      resetState(getAlertBuilder(alert));
    }
  }, [alert, resetState]);

  return (
    <SplitViewPane
      open={isOpen}
      onClose={onClose}
      title={
        <div className="flex w-full items-center justify-between">
          <div>{alert?.rule.name ?? ''}</div>
          <div className="flex gap-2">
            <button
              type="button"
              className="text-button-secondary rounded-md border border-secondary px-3 py-1.5 text-sm font-medium hover:bg-secondary"
              onClick={() => {
                onClose();
                const projectsLink = `/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${sessionId}`;
                navigate(projectsLink);
              }}
            >
              View All Runs
            </button>
            <button
              type="button"
              className="text-button-secondary rounded-md border border-secondary px-3 py-1.5 text-sm font-medium hover:bg-secondary"
              onClick={() => {
                onClose();
                if (alert) {
                  onEditClick(alert);
                }
              }}
            >
              Edit Alert
            </button>
          </div>
        </div>
      }
      sidePaneId="custom-charts-crud-pane"
      className="relative"
      hideArrows
    >
      <PanelGroup direction="horizontal" className="-mx-4 flex-1">
        <Panel
          defaultSize={30}
          minSize={22}
          className="flex flex-col overflow-hidden p-5"
        >
          <AlertPreview sessionId={sessionId} hideHeader withRunsTable />
        </Panel>
      </PanelGroup>
    </SplitViewPane>
  );
}

export function AlertsEditPane({
  isOpen,
  onClose,
  alert,
  sessionId,
  projectName,
}: {
  isOpen: boolean;
  onClose: () => void;
  alert: AlertRuleWithActions;
  sessionId: string;
  projectName?: string;
}) {
  const state = useAlertState();

  const onUpdateSuccess = () => {
    onClose();
  };

  const { trigger: updateAlert, isMutating: isUpdating } =
    useUpdateAlertMutation(sessionId, alert.rule.id ?? '', onUpdateSuccess, {
      onError: (err) =>
        state.setBanner(getErrorBanner('Error updating alert', err)),
    });

  const onSubmit = async () => {
    const request = buildRequest(state);
    if (request instanceof Error) {
      state.setBanner({
        title: 'Error updating alert',
        msg: request.message,
        status: 'error',
      });
    } else {
      await updateAlert({ json: request as UpdateAlertRequest });
    }
  };

  const isUpdatingDisabled = !canUpdateOrCreateAlert(state);

  return (
    <Pane
      open={isOpen}
      onClose={onClose}
      title="Edit Alert"
      className="px-4 pb-0 pt-0"
    >
      <div className="flex h-full">
        <div className="border-divider flex flex-1 flex-col gap-8 overflow-y-auto border-r border-secondary p-6 pb-10">
          <AlertsPaneContent
            projectName={projectName}
            sessionId={sessionId}
            alert={alert}
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="text-button-secondary rounded-md border border-secondary px-3 py-1.5 text-sm font-medium hover:bg-tertiary"
              onClick={onClose}
            >
              Discard Changes
            </button>
            <DisabledTooltip
              disabled={isUpdatingDisabled}
              disabledMsg="Please complete all fields to update this alert."
            >
              <button
                type="button"
                className="flex items-center gap-2 rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-button-primary hover:bg-brand-primary-hover disabled:pointer-events-none disabled:opacity-50"
                onClick={onSubmit}
                disabled={isUpdatingDisabled}
              >
                {isUpdating && (
                  <Loading01Icon className="h-4 w-4 animate-spin" />
                )}
                <span className="text-sm">Save</span>
              </button>
            </DisabledTooltip>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <AlertPreview sessionId={sessionId} />
        </div>
      </div>
    </Pane>
  );
}
